import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, RotateCcw, Square, Search, Users } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Motion";

const SEQUENCES = [
  { id: "kick", name: "Kick fight", dur: 25 },
  { id: "full", name: "Full fight", dur: 60 },
  { id: "combo", name: "Kick combo KO", dur: 25 },
  { id: "scorpion", name: "Scorpion showcase", dur: 30 },
];

const MOVES = [
  { n: 1, name: "Roundhouse — high", pack: "Muay Thai", type: "Paired" },
  { n: 2, name: "Jab / cross counter", pack: "Boxing", type: "Paired" },
  { n: 3, name: "Spinning back kick", pack: "Taekwondo", type: "Kick" },
  { n: 4, name: "Sweep takedown", pack: "Judo", type: "Paired" },
  { n: 5, name: "Axe kick — descending", pack: "Taekwondo", type: "Kick" },
  { n: 6, name: "Elbow clinch break", pack: "Muay Thai", type: "Paired" },
  { n: 7, name: "Scorpion spear finish", pack: "Showcase", type: "Paired" },
  { n: 8, name: "Knockout — rear fall", pack: "Core", type: "Solo" },
];

const SPEEDS = [0.25, 0.5, 1, 1.5];
const COUNTDOWNS = [0, 3, 5, 10];

export const StudioPreview = () => {
  const [seq, setSeq] = useState(SEQUENCES[0]);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [countdown, setCountdown] = useState(0);
  const [ticking, setTicking] = useState(0);
  const raf = useRef();

  useEffect(() => {
    if (!ticking) return;
    const id = setTimeout(() => {
      if (ticking === 1) {
        setTicking(0);
        setPlaying(true);
      } else {
        setTicking(ticking - 1);
      }
    }, 1000);
    return () => clearTimeout(id);
  }, [ticking]);

  useEffect(() => {
    if (!playing) return;
    let last = performance.now();
    const loop = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      setT((prev) => {
        const next = prev + dt * speed;
        if (next >= seq.dur) {
          setPlaying(false);
          return seq.dur;
        }
        return next;
      });
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
  }, [playing, speed, seq]);

  const pct = Math.min(100, (t / seq.dur) * 100);
  const activeMove = Math.min(MOVES.length, Math.floor((pct / 100) * MOVES.length) + (pct > 0 ? 1 : 0));

  const start = () => {
    if (playing) {
      setPlaying(false);
      return;
    }
    if (pct >= 100) setT(0);
    if (countdown > 0) setTicking(countdown);
    else setPlaying(true);
  };

  return (
    <section className="relative border-b border-white/8 py-24 lg:py-36">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid gap-10 pb-12 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <SectionLabel index="§ 02">The interface</SectionLabel>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-display text-[clamp(2rem,4.6vw,3.6rem)] font-extrabold uppercase leading-[0.96] tracking-[-0.015em] text-white">
                Open the menu.
                <br />
                <span className="text-white/35">Direct the whole fight.</span>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:pb-3">
            <Reveal delay={0.12}>
              <p className="text-sm leading-relaxed text-white/45">
                A live taste of the in-game panel. Load a saved sequence, scrub the timeline, drop to
                0.25x for the impact frame, and set a start countdown so you're behind the camera
                before the first punch lands.
              </p>
            </Reveal>
          </div>
        </div>

        <Reveal y={40}>
          <div
            data-testid="studio-preview"
            className="grain relative overflow-hidden border border-white/12 bg-[#0d0d11]"
          >
            <div className="flex items-center justify-between border-b border-white/10 bg-[#111116] px-4 py-3">
              <div className="flex items-center gap-3">
                <span className="h-2 w-2 rounded-full bg-[hsl(var(--orv-blue))]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/60">
                  Orvyn Fight Studio · v4.2.0
                </span>
              </div>
              <span className="font-mono text-[10px] tracking-[0.18em] text-white/30">
                Standalone
              </span>
            </div>

            <div className="grid lg:grid-cols-12">
              {/* library */}
              <div className="border-b border-white/10 p-5 lg:col-span-5 lg:border-b-0 lg:border-r">
                <div className="mb-4 flex items-center gap-2 border border-white/10 bg-black/40 px-3 py-2">
                  <Search className="h-3 w-3 text-white/30" />
                  <span className="font-mono text-[10px] tracking-[0.14em] text-white/30">
                    Search 262 animations…
                  </span>
                </div>
                <div className="mb-4 flex flex-wrap gap-2">
                  {["Kicks", "Paired", "In sequence"].map((f) => (
                    <span
                      key={f}
                      className="border border-white/10 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.16em] text-white/45"
                    >
                      {f}
                    </span>
                  ))}
                </div>
                <div className="divide-y divide-white/6">
                  {MOVES.map((m) => {
                    const on = playing && m.n === activeMove;
                    return (
                      <div
                        key={m.n}
                        className={`flex items-center gap-3 px-1 py-2.5 transition-colors duration-300 ${
                          on ? "bg-[hsl(var(--orv-blue))]/12" : ""
                        }`}
                      >
                        <span
                          className={`grid h-6 w-6 shrink-0 place-items-center border font-mono text-[10px] transition-colors duration-300 ${
                            on
                              ? "border-[hsl(var(--orv-blue))] text-[hsl(var(--orv-blue))]"
                              : "border-white/12 text-white/40"
                          }`}
                        >
                          {m.n}
                        </span>
                        <span className={`flex-1 truncate text-[13px] ${on ? "text-white" : "text-white/60"}`}>
                          {m.name}
                        </span>
                        <span className="hidden font-mono text-[9px] uppercase tracking-[0.14em] text-white/25 sm:block">
                          {m.pack}
                        </span>
                        <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-white/25">
                          {m.type}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* stage + controls */}
              <div className="lg:col-span-7">
                <div className="relative aspect-[16/9] overflow-hidden border-b border-white/10 bg-[#080809]">
                  <div className="hairline-grid absolute inset-0 opacity-50" />
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_60%,rgba(59,130,246,0.1),transparent_62%)]" />

                  {/* spectators */}
                  <div className="absolute inset-x-0 top-[18%] flex justify-center gap-7 opacity-30">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <motion.span
                        key={i}
                        animate={playing ? { y: [0, -4, 0] } : { y: 0 }}
                        transition={{
                          duration: 0.9,
                          repeat: playing ? Infinity : 0,
                          delay: i * 0.12,
                          ease: "easeInOut",
                        }}
                        className="h-5 w-5 rounded-full border border-white/40"
                      />
                    ))}
                  </div>

                  {/* actor markers */}
                  <motion.div
                    animate={playing ? { x: [0, 26, -8, 18, 0], y: [0, -6, 4, -3, 0] } : { x: 0, y: 0 }}
                    transition={{ duration: 3.4 / speed, repeat: playing ? Infinity : 0, ease: "easeInOut" }}
                    className="absolute left-[30%] top-[58%] -translate-y-1/2"
                  >
                    <div className="grid h-14 w-14 place-items-center rounded-full border border-white/25">
                      <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-white/70">A</span>
                    </div>
                    <span className="mt-2 block text-center font-mono text-[8px] uppercase tracking-[0.2em] text-white/30">
                      Attacker
                    </span>
                  </motion.div>
                  <motion.div
                    animate={playing ? { x: [0, -14, 6, -10, 0], rotate: [0, -4, 3, -6, 0] } : { x: 0, rotate: 0 }}
                    transition={{ duration: 3.4 / speed, repeat: playing ? Infinity : 0, ease: "easeInOut" }}
                    className="absolute left-[58%] top-[58%] -translate-y-1/2"
                  >
                    <div className="grid h-14 w-14 place-items-center rounded-full border border-[hsl(var(--orv-blue))]/55">
                      <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-[hsl(var(--orv-blue))]">
                        V
                      </span>
                    </div>
                    <span className="mt-2 block text-center font-mono text-[8px] uppercase tracking-[0.2em] text-white/30">
                      Victim
                    </span>
                  </motion.div>

                  <div className="absolute left-4 top-4 font-mono text-[9px] uppercase tracking-[0.2em] text-white/35">
                    {seq.name}
                  </div>
                  <div
                    className="absolute right-4 top-4 font-mono text-[9px] tracking-[0.18em] text-white/35"
                    data-testid="studio-time"
                  >
                    {t.toFixed(2)}s / {seq.dur}.00s
                  </div>
                  <div className="absolute bottom-4 left-4 flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.2em] text-white/25">
                    <Users className="h-3 w-3" />
                    Crowd · cheering
                  </div>
                  {!playing && pct >= 100 && (
                    <div className="absolute bottom-4 right-4 font-mono text-[9px] uppercase tracking-[0.2em] text-[hsl(var(--orv-blue))]">
                      Knockout applied
                    </div>
                  )}

                  <AnimatePresence>
                    {ticking > 0 && (
                      <motion.div
                        key={ticking}
                        initial={{ opacity: 0, scale: 1.4 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.85 }}
                        transition={{ duration: 0.35 }}
                        data-testid="studio-countdown"
                        className="absolute inset-0 grid place-items-center font-display text-7xl font-extrabold text-white/80"
                      >
                        {ticking}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <div className="p-5">
                  <div className="mb-5 flex flex-wrap gap-2">
                    {SEQUENCES.map((s) => (
                      <button
                        key={s.id}
                        data-testid={`fight-${s.id}`}
                        onClick={() => {
                          setSeq(s);
                          setT(0);
                          setPlaying(false);
                          setTicking(0);
                        }}
                        className={`border px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.14em] transition-colors duration-300 ${
                          seq.id === s.id
                            ? "border-white/60 bg-white/8 text-white"
                            : "border-white/10 text-white/40 hover:border-white/30 hover:text-white/75"
                        }`}
                      >
                        {s.name} · {s.dur}s
                      </button>
                    ))}
                  </div>

                  <div
                    role="button"
                    tabIndex={0}
                    data-testid="timeline-scrubber"
                    onClick={(e) => {
                      const r = e.currentTarget.getBoundingClientRect();
                      setT(((e.clientX - r.left) / r.width) * seq.dur);
                    }}
                    className="group relative mb-5 h-8 cursor-pointer"
                  >
                    <div className="absolute top-1/2 h-[3px] w-full -translate-y-1/2 bg-white/10" />
                    <div
                      className="absolute top-1/2 h-[3px] -translate-y-1/2 bg-[hsl(var(--orv-blue))]"
                      style={{ width: `${pct}%` }}
                    />
                    <div
                      className="absolute top-1/2 h-3.5 w-[2px] -translate-y-1/2 bg-white transition-transform duration-200 group-hover:scale-y-150"
                      style={{ left: `${pct}%` }}
                    />
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      data-testid="studio-play-button"
                      onClick={start}
                      className="flex items-center gap-2 bg-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-black transition-colors duration-300 hover:bg-[hsl(var(--orv-blue))]"
                    >
                      {playing ? <Pause className="h-3 w-3 fill-current" /> : <Play className="h-3 w-3 fill-current" />}
                      {playing ? "Pause" : "Play"}
                    </button>
                    <button
                      data-testid="studio-stop-button"
                      onClick={() => {
                        setPlaying(false);
                        setTicking(0);
                        setT(0);
                      }}
                      className="flex items-center gap-2 border border-white/12 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-white/60 transition-colors duration-300 hover:border-white/35 hover:text-white"
                    >
                      <Square className="h-3 w-3 fill-current" />
                      Stop
                    </button>
                    <button
                      data-testid="studio-replay-button"
                      onClick={() => {
                        setTicking(0);
                        setT(0);
                        setPlaying(true);
                      }}
                      className="flex items-center gap-2 border border-white/12 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-white/60 transition-colors duration-300 hover:border-white/35 hover:text-white"
                    >
                      <RotateCcw className="h-3 w-3" />
                      Replay
                    </button>

                    <div className="ml-auto flex items-center gap-1 border border-white/10 p-1">
                      {SPEEDS.map((s) => (
                        <button
                          key={s}
                          data-testid={`speed-${s}`}
                          onClick={() => setSpeed(s)}
                          className={`px-2.5 py-1 font-mono text-[10px] transition-colors duration-200 ${
                            speed === s ? "bg-white text-black" : "text-white/45 hover:text-white"
                          }`}
                        >
                          {s}x
                        </button>
                      ))}
                    </div>

                    <div className="flex items-center gap-1 border border-white/10 p-1">
                      {COUNTDOWNS.map((c) => (
                        <button
                          key={c}
                          data-testid={`countdown-${c}`}
                          onClick={() => setCountdown(c)}
                          className={`px-2.5 py-1 font-mono text-[10px] transition-colors duration-200 ${
                            countdown === c ? "bg-white text-black" : "text-white/45 hover:text-white"
                          }`}
                        >
                          {c === 0 ? "Now" : `${c}s`}
                        </button>
                      ))}
                    </div>
                  </div>

                  <p className="mt-4 font-mono text-[10px] leading-relaxed tracking-[0.06em] text-white/25">
                    Illustrative preview of the in-game panel — the real menu adds guided placement,
                    the character editor and full crowd staging.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
